import { BulkActionBar } from '@app/components/data-table/bulk-edit/bulk-action-bar';
import useAuth from '@app/auth/use-auth';
import { makeSelectColumn } from '@app/components/data-table/bulk-edit/select-column';
import { DataTable } from '@app/components/data-table/data-table';
import { DataTableSortList } from '@app/components/data-table/data-table-sort-list';
import { DataTableToolbar } from '@app/components/data-table/data-table-toolbar';
import { useServerDataTable } from '@app/components/data-table/hooks/use-server-data-table';
import {
  FilterOptions,
  useServerFilterOptions,
} from '@app/components/data-table/hooks/use-server-filter-options';
import { DataTableProps } from '@app/components/data-table/types/data-table';
import { getReportsPathMap } from '@app/components/reports/utils';
import {
  FoodFilterColumn,
  foodFilterKeys as purchasedFoodFilterKeys,
  FoodFilterOption as PurchasedFoodFilterOption,
  foodFilters as purchasedFoodFilters,
} from '@app/purchased-goods/food-and-drink/filters';
import { useAnalytics } from '@app/services/analytics/AnalyticsContext';
import {
  foodFilterKeys as soldFoodFilterKeys,
  FoodFilterOption as SoldFoodFilterOption,
  foodFilters as soldFoodFilters,
} from '@app/sold-goods/filters';
import { usePatchPurchasedFoodAndDrinkItem } from '@shared/api/hooks/purchased-goods/food-and-drink';
import { usePatchSoldFoodAndDrinkItem } from '@shared/api/hooks/sold-goods/food-and-drink';
import {
  FoodActivitiesList,
  FoodOperationActivitiesList,
  OperationReportTypeEnum,
  PaginatedFoodActivitiesListList,
} from '@shared/api/types';
import { SupportButton } from '@shared/components/buttons/support-button';
import { useToast } from '@shared/components/ui/use-toast';
import { parseAsInteger, useQueryState } from 'nuqs';
import { useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { makeBulkConfirmAction } from './bulk/bulk-confirm-modal-body';
import { makeBulkUpdateMatchedFoodAction } from './bulk/bulk-update-matched-food-modal-body';
import { useFoodReviewColumns } from './food-review-columns';

interface CleanedDateTableProps
  extends DataTableProps<PaginatedFoodActivitiesListList> {
  reportType: OperationReportTypeEnum;
  data?: PaginatedFoodActivitiesListList;
}

export const FoodReviewTable = ({
  data,
  reportType,
  isLoading,
  isPreviousData,
  isFetchedAfterMount,
}: CleanedDateTableProps) => {
  const { toast } = useToast();
  const { track } = useAnalytics();
  const [perPage] = useQueryState('perPage', parseAsInteger.withDefault(10));
  const navigate = useNavigate();
  const { count } = data || {};
  const pageCount = count ? Math.ceil(count / (perPage || 10)) : 0;
  const [updatingRowId, setUpdatingRowId] = useState<
    FoodActivitiesList['uuid'] | null
  >(null);

  const isPurchase = reportType === OperationReportTypeEnum.purchase;
  const { user } = useAuth();

  const { mutateAsync: patchPurchased } = usePatchPurchasedFoodAndDrinkItem();
  const { mutateAsync: patchSold } = usePatchSoldFoodAndDrinkItem();
  const updateFoodActivity = isPurchase ? patchPurchased : patchSold;

  const foodFilterKeys = isPurchase
    ? purchasedFoodFilterKeys
    : soldFoodFilterKeys;
  const foodFilters = isPurchase ? purchasedFoodFilters : soldFoodFilters;

  const onEdit = (rowId: FoodActivitiesList['uuid']) => {
    track('Edited Food Review Row', { reportType });
    navigate(
      generatePath(getReportsPathMap()[reportType].foodItem!, {
        item_uuid: rowId,
      })
    );
  };

  const onConfirm = async (rowId: FoodActivitiesList['uuid']) => {
    setUpdatingRowId(rowId);
    try {
      await updateFoodActivity({
        activityUuid: rowId,
        data: { userReviewed: true },
      });
      track('Confirmed Food Review Row', { reportType });
    } catch (error) {
      toast({
        title: 'Error confirming food item',
        description:
          'There was an error confirming the food item. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setUpdatingRowId(null);
    }
  };

  const filterOptions = useServerFilterOptions(
    foodFilterKeys,
    data?.filters || {}
  );

  const useFilterOptions = useMemo(() => {
    const opts: FilterOptions<
      (PurchasedFoodFilterOption | SoldFoodFilterOption)[]
    > = {} as FilterOptions<
      (PurchasedFoodFilterOption | SoldFoodFilterOption)[]
    >;
    foodFilterKeys.forEach((key) => {
      opts[key] = filterOptions[`${key}Filter`];
    });
    return opts;
  }, [filterOptions, foodFilterKeys]);

  const reviewColumns = useFoodReviewColumns({
    reportType: reportType as OperationReportTypeEnum,
    onEdit,
    onConfirm,
    updatingRowId: updatingRowId,
    filters: useFilterOptions,
  });

  // Both report types get the same bulk actions — the sold bulk endpoint
  // mirrors the purchased one.
  const bulkActions = useMemo(
    () => [
      makeBulkConfirmAction({ reportType }),
      makeBulkUpdateMatchedFoodAction({ reportType }),
    ],
    [reportType]
  );

  const { table } = useServerDataTable({
    data: data?.results || [],
    columns: user?.isStaff
      ? [
          // Selection checkboxes are staff-only (bulk actions are gated to staff).
          makeSelectColumn<FoodActivitiesList | FoodOperationActivitiesList>(),
          ...reviewColumns,
        ]
      : reviewColumns,
    pageCount,
    shallow: false,
    getRowId: (row) => row.uuid,
    initialState: {
      columnVisibility: {
        id: false,
        errors: false,
        reviewType: false,
        emissions: false,
        [FoodFilterColumn.ActivitiesImport]: false,
        [FoodFilterColumn.Created]: false,
        [FoodFilterColumn.RemoteId]: false,
        [FoodFilterColumn.Supplier]: false,
      },
    },
  });

  return (
    <>
      <DataTable
        table={table}
        className="border border-gray-100 bg-gray-00 text-left"
        isLoading={(!isFetchedAfterMount && isPreviousData) || isLoading}
        columnClassName={{
          uploadedData: '!bg-primary-50 border-r border-primary-100',
          inputUnit: 'border-r border-primary-100',
          processedData: 'bg-none',
          review: '!bg-teal-50 border-l border-teal-100',
          confirm: 'border-l border-teal-100',
        }}
      >
        <DataTableToolbar
          table={table}
          filterOrder={[
            'food',
            ...Object.values(foodFilters).map((f) => f.column),
          ]}
        >
          <SupportButton article="foodReview" size="sm">
            Instructions
          </SupportButton>
          <DataTableSortList table={table} align="start" className="ml-auto" />
        </DataTableToolbar>
      </DataTable>
      <BulkActionBar table={table} actions={bulkActions} />
    </>
  );
};
